import {
  isAllowedMaxLoadCap,
  isTrailerMinBill,
  resolveMinBillable,
  type TrailerMinBill,
} from '@features/freight/types/freight.types';

// Values mirror the backend QuotationStatusEnum (int). Keep them as numeric literals
// so they round-trip through JSON without lossy conversions.
export enum QuotationStatus {
  Draft = 0,
  Submitted = 1,
  // Created by the email automation pipeline. Editable; tracked in its own bucket so
  // the existing "Drafts" tab keeps showing only Status=Draft (manual drafts).
  AutoDraft = 2,
  // Quotation has been emailed to the client. Read-only, parity with Submitted.
  Sent = 3,
  // 86d3mnuxe — Posting/PostingFailed removed. Posting-in-flight + failed are OUTBOX states now, read via
  // GET /api/outbox/status (fetchOutboxStatus, key quotation:{id}). The draft stays Draft/AutoDraft until
  // the post succeeds (→ Submitted).
}

// Mirrors backend ExtractionStatusEnum (the persisted column on Quotation). Drives the
// AutoDraft inbox banner and the review-panel retry control. Manual drafts always carry
// NotApplicable. Distinct from the manual-intake-flow `ExtractionStatus` string union
// (idle/processing/completed/failed) which lives below — that one tracks transient UI
// state during a single paste/upload session.
export enum EmailExtractionStatus {
  NotApplicable = 0,
  Pending = 1,
  Completed = 2,
  SkippedAmbiguous = 3,
  Failed = 4,
}

/** NetSuite customlist_separatebilling (custbody_quote_pickup) — values = NetSuite internal IDs. */
export enum PickupType {
  Yes = '1',
  No = '2',
  FreightIncorporated = '3',
}

export const PICKUP_TYPE_OPTIONS: Array<{ value: PickupType; label: string }> = [
  { value: PickupType.Yes, label: 'Yes — customer pick-up' },
  { value: PickupType.No, label: 'No — delivery' },
  { value: PickupType.FreightIncorporated, label: 'Freight Incorporated' },
];

/** NetSuite customlist563 (custbody_csc_waivedfreight) — values = NetSuite internal IDs. */
export enum WaivedFreight {
  Yes = '1',
  No = '2',
}

export const WAIVED_FREIGHT_OPTIONS: Array<{ value: WaivedFreight; label: string }> = [
  { value: WaivedFreight.Yes, label: 'Yes' },
  { value: WaivedFreight.No, label: 'No' },
];

/** NetSuite price level internalIds. */
export enum PriceLevel {
  BasePrice = '1',
  AveragePrice = '2',
  Custom = '-1',
}

export const PRICE_LEVEL_OPTIONS: Array<{ value: PriceLevel; label: string }> = [
  { value: PriceLevel.BasePrice, label: 'Base Price' },
  { value: PriceLevel.AveragePrice, label: 'Average Price' },
  { value: PriceLevel.Custom, label: 'Custom' },
];

export const DEFAULT_PRICE_LEVEL: PriceLevel = PriceLevel.BasePrice;


export type LineItemMatchStatus =
  | 'MATCHED'
  | 'NEEDS_CLARIFICATION'
  | 'NO_MATCH'
  | 'SUMMARY_ONLY'
  | 'EXTERNAL_ATTACHMENT';

export interface LineItemPossibleMatch {
  itemId: string;
  lengthM: number;
  salesDescription: string;
  weightPerPcKg: number;
}

export interface LineItemSpecSnapshot {
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  /** QUO-R-004 (14ykpqtw3hw): the NORMALIZED quantity — what Revert returns to. */
  quantity: number | null;
  unit: 'pcs' | 'kgs';
  /** QUO-R-004: the customer's own quantity, present only when the cutting-list rule rounded or converted
   *  the line (or could not convert a kg order). Drives the "asked 100.2" note under the quantity. */
  sourceQuantity?: number | null;
  sourceUnit?: 'pcs' | 'kgs' | null;
}

export interface QuotationLineItemDto {
  id: string;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: 'pcs' | 'kgs';
  weightKg: number | null;
  matchStatus?: LineItemMatchStatus;
  rawDescription?: string | null;
  notes?: string | null;
  matchedSalesDescription?: string | null;
  matchedItemId?: string | null;
  possibleMatches?: LineItemPossibleMatch[];
  /** NetSuite price level internalId; defaults to Base Price. */
  priceLevel?: PriceLevel;
  /** Per-piece Base Price snapshot from matched item — null if not matched. */
  basePricePerPc?: number | null;
  /** Per-piece Average Price snapshot from matched item — null if not matched. */
  averagePricePerPc?: number | null;
  /** Snapshot of the original extracted spec — null for user-added rows. */
  originalSpec?: LineItemSpecSnapshot | null;
  isEdited?: boolean;
  /** Transient UI state — stripped on persist. */
  isMatching?: boolean;
  /** Transient UI state — stripped on persist. */
  matchError?: string | null;
  /**
   * CSC-291: pre-promotion grade if `GradePromotionRules` fired at extraction time.
   * Null on lines that were not promoted. Drives the per-line "Promoted from G{n}" badge
   * and the workspace banner aggregates over all lines that carry this.
   */
  promotedFromGrade?: number | null;
}

export const EDITABLE_DIAMETERS_MM = [6, 8, 10, 12, 16, 20, 25, 28, 32, 36, 40, 50] as const;
export const EDITABLE_LENGTHS_M = [2, 3, 4, 5, 6, 7.5, 9, 10.5, 12, 13.5, 15, 18] as const;
// G33 is excluded from manual selection — it's not a sellable grade: extraction auto-promotes
// G33→G40 (GradePromotionRules) and it has no base markup, so a manually-set G33 line would post
// at cost. The `promotedFromGrade` "G33↑" hint still renders for auto-promoted lines. 86d3j5qca.
export const EDITABLE_GRADES = ['40', '60', '75', '80'] as const;

export type ExtractionStatus = 'idle' | 'processing' | 'completed' | 'failed';

export type ExtractionSourceKind = 'text' | 'file';

/** Notice surfaced when the backend normalized missing quantities during extraction (CSC-265). */
export type ExtractionNotice =
  | { kind: 'all-defaulted'; count: number }
  | { kind: 'partial-dropped'; count: number }
  /**
   * CSC-291: one or more line items were auto-promoted to a higher grade per CSC's
   * extraction-time policy. Aggregated by `(from, to)` pair so the banner can render
   * `G33→G40 (3) · 16mm G40→G60 (1)`.
   */
  | { kind: 'grades-promoted'; promotions: Array<{ from: number; to: number; count: number }> }
  /**
   * 14ykpqtw3hw QUO-R-003: kg lines that did not become quotation lines, by reason — repeats of a pieces
   * line and total rows (dropped by the server), and figures without a full size and length (dropped here).
   */
  | { kind: 'kg-dropped'; repeats: number; totals: number; figures: number };

/**
 * Single attachment surfaced on the AutoDraft review card. Bytes are NOT included —
 * download is triggered via `/api/email-automation/attachment/{emailId}/{id}` on click.
 */
export interface QuotationSourceEmailAttachment {
  id: number;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
  /** Backend AttachmentClassifier flagged this as a likely signature/inline asset
   *  (Outlook image00X, logo.png, vcard, small image). Excluded from auto-extraction;
   *  rendered greyed-out with a "signature" badge in the review panel. */
  isLikelySignature: boolean;
}

/**
 * Snapshot of the inbound email an AutoDraft was created from. Populated only on
 * AutoDraft rows; null on manual Drafts and Submitted/Sent rows.
 */
export interface QuotationSourceEmailSummary {
  id: number;
  gmailMessageId: string;
  gmailThreadId: string;
  from: string;
  subject: string;
  receivedAt: string;
  bodyText: string | null;
  attachments: QuotationSourceEmailAttachment[];
}

/**
 * CSC-294: full Gmail conversation snapshot rendered in the quotation review
 * panel's Conversation tab. Hydrated lazily by gmailThreadService — prefetched
 * on master-list row hover so it's hot when sales clicks Review.
 */
export interface GmailThreadDto {
  threadId: string;
  messages: GmailThreadMessageDto[];
}

export interface GmailThreadMessageDto {
  messageId: string;
  from: string;
  to: string;
  cc: string | null;
  /** ISO-8601 UTC timestamp from the backend (DateTime serialized by ASP.NET). */
  date: string;
  subject: string;
  /** Plain-text body. May be null if the source message was HTML-only. */
  bodyText: string | null;
  /** Server-sanitized HTML body. Safe to render via dangerouslySetInnerHTML. */
  bodyHtml: string | null;
  /** True for received messages, false for messages sent from the sales mailbox. */
  isInbound: boolean;
}

/**
 * Manually-uploaded attachment surfaced on a Draft (CSC-283 Layer 3 multi-file flow).
 * Disjoint from `sourceEmail.attachments` — those come from the email pipeline; these
 * come from the multi-file intake form. Bytes lazy-fetched via
 * `/api/quotations/{quotationId}/attachment/{id}`.
 */
export interface QuotationAttachment {
  id: number;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
}

// ── 86d32r9rv Phase 5 — FreightCharge discriminated union (TS mirror) ─────────
// Mirrors CSC_CORE.Features.Quotations.Objects.Freight.FreightCharge. The shape
// makes invalid states unrepresentable: MetroManilaFlat requires a destination,
// so the QD-202605-00207 / QD-202605-00218 state (MM ticked + null destination)
// cannot exist on a parsed instance.
//
// Persisted as JSON in two columns:
//   - Quotation.FreightChargeJson      — mutable working value during editing.
//   - Quotation.FreightSnapshotJson    — frozen-at-submit copy (write-once).
// Both columns hold the same shape; the snapshot's immutability is enforced by
// service-layer rules, not by the type.

export interface FreightDestinationRef {
  id: string;
  name: string;
  cityName: string;
}

export type FreightCharge =
  | {
      kind: 'MetroManilaFlat';
      destination: FreightDestinationRef;
      truck: string; // "trailer" | "10wh"
      flatRatePerTruck: number;
      numTrucks: number;
      /** Exact formula result — what sales' rate card says. */
      computedAmount: number;
      /** computedAmount rounded up to the nearest ₱1,000 — what hits NetSuite grossAmt. */
      billedAmount: number;
    }
  | {
      kind: 'PerTonDopRate';
      destination: FreightDestinationRef;
      truck: string;
      ratePerTon: number;
      discountPerTon: number;
      tollPerTruck: number;
      numTrucks: number;
      padding: number;
      /** 14ykpqtvw4a — per-truck floor this charge billed at (trailer 40/45/50, 10wh 20). Filled for
       *  JSON written before the field existed by `parseFreightCharge`. */
      minBillPerTruck: number;
      computedAmount: number;
      billedAmount: number;
      /** 14ykpqtw3ua — the max load per truck this charge was sized at, recorded only when the cap changed the
       *  truck count; null when none applied (and for JSON written before the field existed). `parseFreightCharge`
       *  normalizes a missing or invalid value to null. */
      maxLoadPerTruck: number | null;
    }
  | { kind: 'Waived' }
  | { kind: 'None' };

/**
 * Raw freight-cabinet inputs the salesperson is editing. Mirrors
 * CSC_CORE.Features.Quotations.Objects.Freight.FreightInputDraftDto. Persisted as
 * the JSON value of Quotation.FreightInputDraftJson on every save so partial
 * mid-edit state survives reload / crash. Distinct from `FreightCharge`: this
 * shape captures intent (what sales has clicked so far); the union captures the
 * validated outcome (what would actually hit NetSuite on submit).
 *
 * Field names use the same hyphen-snake-cased camelCase the wire layer uses (matches
 * the backend's [JsonPropertyName] convention via System.Text.Json camelCase policy).
 */
export interface FreightInputDraft {
  withinMetroManila: boolean;
  freightDestinationId: string | null;
  freightTruckType: 'trailer' | '10wh' | null;
  freightDiscount: number | null;
  freightTollPerTruck: number | null;
  freightAllocationSummary: string | null;
  waivedFreight: WaivedFreight;
  /**
   * 14ykpqtvw4a — the trailer minimum sales chose. Null = never chosen: bills at the legacy 40, which is
   * how quotations priced before the choice existed keep their price. The cabinet writes an explicit
   * value when a destination is picked, so a quotation priced from scratch is saved with its 45.
   */
  freightTrailerMinBillTons: TrailerMinBill | null;
  /**
   * 14ykpqtw3ua — the max load per truck sales typed, for a destination that cannot take a fully loaded
   * truck. Null = no cap, which is how every quotation saved before the field existed keeps its price.
   * Otherwise a whole number of tons ≥ 1 (`isAllowedMaxLoadCap`) — the server refuses anything else, so
   * the parse below drops it. Kept as typed; the charge records only a cap that applied.
   */
  freightMaxLoadPerTruck: number | null;
}

export const EMPTY_FREIGHT_INPUT_DRAFT: FreightInputDraft = {
  withinMetroManila: false,
  freightDestinationId: null,
  freightTruckType: null,
  freightDiscount: null,
  freightTollPerTruck: null,
  freightAllocationSummary: null,
  waivedFreight: WaivedFreight.No,
  freightTrailerMinBillTons: null,
  freightMaxLoadPerTruck: null,
};

/** Tolerant parse — malformed JSON / unknown shapes resolve to a blank input draft. */
export function parseFreightInputDraft(json: string | null | undefined): FreightInputDraft {
  if (!json) return EMPTY_FREIGHT_INPUT_DRAFT;
  try {
    const parsed = JSON.parse(json) as Partial<FreightInputDraft> | null;
    if (!parsed || typeof parsed !== 'object') return EMPTY_FREIGHT_INPUT_DRAFT;
    const truck = parsed.freightTruckType;
    return {
      withinMetroManila: parsed.withinMetroManila === true,
      freightDestinationId: parsed.freightDestinationId ?? null,
      freightTruckType: truck === 'trailer' || truck === '10wh' ? truck : null,
      freightDiscount: typeof parsed.freightDiscount === 'number' ? parsed.freightDiscount : null,
      freightTollPerTruck:
        typeof parsed.freightTollPerTruck === 'number' ? parsed.freightTollPerTruck : null,
      freightAllocationSummary: parsed.freightAllocationSummary ?? null,
      waivedFreight: parsed.waivedFreight === WaivedFreight.Yes ? WaivedFreight.Yes : WaivedFreight.No,
      // Only the three allowed values survive; anything else reads as "never chosen" rather than
      // reaching the server, which would refuse the save.
      freightTrailerMinBillTons: isTrailerMinBill(parsed.freightTrailerMinBillTons)
        ? parsed.freightTrailerMinBillTons
        : null,
      // Same idea for the max load per truck: only a whole number ≥ 1 survives, as a JSON number. A string
      // would make the server's parse fall back to a BLANK draft and wipe the whole freight selection.
      freightMaxLoadPerTruck: isAllowedMaxLoadCap(parsed.freightMaxLoadPerTruck)
        ? parsed.freightMaxLoadPerTruck
        : null,
    };
  } catch {
    return EMPTY_FREIGHT_INPUT_DRAFT;
  }
}

export function serializeFreightInputDraft(input: FreightInputDraft): string {
  return JSON.stringify(input);
}

/** Tolerant parse for the validated FreightCharge union; returns null on bad input.
 *
 *  Carries a legacy-shape shim: pre-86d332med JSON has a bare `totalAmount` instead of
 *  the `computedAmount` / `billedAmount` split. When only the legacy field is present,
 *  populate both new fields from it so post-split consumers see a uniform shape. Mirrors
 *  the [JsonConstructor] shim on the C# FreightCharge variants.
 *
 *  14ykpqtvw4a — a PerTonDopRate written before `minBillPerTruck` existed was billed at the
 *  then-fixed floor (trailer 40, 10wh 20); fill that in, as the C# shim does.
 *
 *  14ykpqtw3ua — `maxLoadPerTruck` is null when missing (JSON written before the field) or not a
 *  positive number; the C# shim binds a missing key to null the same way. */
export function parseFreightCharge(json: string | null | undefined): FreightCharge | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const kind = (parsed as { kind?: unknown }).kind;
    if (
      kind !== 'MetroManilaFlat' &&
      kind !== 'PerTonDopRate' &&
      kind !== 'Waived' &&
      kind !== 'None'
    ) {
      return null;
    }
    if (kind === 'MetroManilaFlat' || kind === 'PerTonDopRate') {
      const raw = parsed as Record<string, unknown>;
      const legacy = typeof raw.totalAmount === 'number' ? raw.totalAmount : 0;
      const computed =
        typeof raw.computedAmount === 'number' && raw.computedAmount > 0
          ? raw.computedAmount
          : legacy;
      const billed =
        typeof raw.billedAmount === 'number' && raw.billedAmount > 0
          ? raw.billedAmount
          : legacy;
      if (kind === 'PerTonDopRate') {
        const minBillPerTruck =
          typeof raw.minBillPerTruck === 'number' && raw.minBillPerTruck > 0
            ? raw.minBillPerTruck
            : resolveMinBillable(String(raw.truck), null);
        const maxLoadPerTruck =
          typeof raw.maxLoadPerTruck === 'number' && raw.maxLoadPerTruck > 0 ? raw.maxLoadPerTruck : null;
        return {
          ...raw,
          computedAmount: computed,
          billedAmount: billed,
          minBillPerTruck,
          maxLoadPerTruck,
        } as FreightCharge;
      }
      return { ...raw, computedAmount: computed, billedAmount: billed } as FreightCharge;
    }
    return parsed as FreightCharge;
  } catch {
    return null;
  }
}

/**
 * 14ykpqtr22u — a narrow, read-only subset of the frozen record of what a posted quotation sent to
 * NetSuite (CSC_CORE QuotationSubmissionSnapshotDto, returned on QuotationDto.SubmissionSnapshot).
 * Units differ by field: `gradeMarkups` and `totals.smallSizePct` are PERCENT (90, 18.5);
 * `totals.tierAdjustmentPct` is a FRACTION (0.04). `totals` sits inside `header`.
 */
export interface QuotationSubmissionSnapshot {
  /** 1 = original shape (no tier/mix recorded); 2 = adds tier / size mix / adjustment. */
  schemaVersion: number;
  header?: {
    /** Effective per-grade markup that posted (base + tier adjustment), whole percent. */
    gradeMarkups?: {
      grade33: number;
      grade40: number;
      grade60: number;
      grade75: number;
      grade80: number;
    } | null;
    totals?: {
      totalWeightKg: number;
      /** Percent (18.5 = 18.5%). Null when the posting had no weight. */
      smallSizePct: number | null;
      tier: string | null;
      sizeMix: string | null;
      /** Fraction (0.04 = +4 points). */
      tierAdjustmentPct: number | null;
      /** The size rule it posted under; absent on snapshots written before the rule was recorded. */
      smallDiametersMm?: number[] | null;
    } | null;
  } | null;
  lines?: Array<{
    matchedItemId: string;
    gradeCode: string | null;
    diameterMm: number | null;
    quantityPc: number;
    lineWeightKg: number | null;
    baseRate: number | null;
    finalUnitPrice: number | null;
  }> | null;
}

export interface QuotationDraft {
  /** Local UUID — stable across renders; also used as the server-side ClientId for line items. */
  id: string;
  /** Server-side numeric id — populated once the draft is persisted. Absent on brand-new drafts before first save. */
  serverId?: number;
  /** Server-side quotation number (Q-DRAFT-#####) — populated after first save. */
  quotationNumber?: string;
  /** Draft vs. Submitted — Submitted renders read-only. */
  status: QuotationStatus;
  /** Snapshot of the base markup applied at save time (decimal, e.g. 0.46 = +46%). */
  baseMarkupPct?: number;
  /** Snapshot of per-grade markups serialized as JSON (whole percents). */
  gradeMarkupsJson?: string | null;
  /** Cutting-list tran id from NetSuite — populated on Submit. */
  cuttingListNumber?: string | null;
  /** NetSuite Estimate browse URL — populated on Submit. */
  netSuiteUrl?: string | null;
  /** ISO timestamp of NetSuite post — populated on Submit. */
  postedAt?: string | null;
  /** Display name of the poster — populated on Submit. */
  postedByName?: string | null;
  /** CSC-287: id of the salesperson currently preparing the draft. Null on
   *  AutoDrafts that no human has saved yet. */
  preparedById?: number | null;
  /** CSC-287: display name of the preparer. Falls back to CreatedByUser when
   *  PreparedByUser is null. */
  preparedByName?: string | null;
  /** Display name of the original creator. Populated for all drafts — system user
   *  for AutoDrafts, the signed-in user who started the draft otherwise. Surfaced by
   *  the detail panel as "Created by …" so submitted drafts retain audit visibility. */
  createdByName?: string | null;
  customerName: string;
  headMainOffice: string;
  /** NetSuite internalId of the selected customer. Null when the compose flow hasn't picked a synced customer yet. */
  customerId: string | null;
  /** NetSuite entityId (customer number / code) of the selected customer. */
  customerEntityId: string | null;
  /** NetSuite internalId of the head / main office. Falls back to customerId when the customer has no parent. */
  headMainOfficeCustomerId: string | null;
  /** NetSuite customercategory internalId. Null until a project category is picked. */
  projectCategoryId: string | null;
  /** Display label for the selected project category. */
  projectCategoryName: string | null;
  projectName: string;
  /** NetSuite term internalId. Null until a term is picked (or pre-selected from customer default). */
  termsId: string | null;
  /** Display label for the selected term. */
  termsName: string | null;
  /** NetSuite employee internalId for the sales rep. Auto-filled from customer.salesrep on customer select; user can override. */
  salesRepId: string | null;
  /** NetSuite employee internalId for the sales coordinator. Auto-filled from customer.custentity_salescoord on customer select; user can override. */
  salesCoordId: string | null;
  /**
   * Tax rate carried over from the selected customer (sourced from
   * taxitemtaxgroup.rate via customer.taxitem). Decimal form — 0.12 = 12%.
   * Used by `computePricing` to derive Gross Amount per line item.
   * Null when no customer is picked or when the customer has no taxitem linked.
   */
  taxRate: number | null;
  date: string;
  escalationDate: string;
  expirationDate: string;
  pickup: PickupType;
  waivedFreight: WaivedFreight;
  // ── 86d32r9rv — freight cabinet state, persisted as raw JSON ──────────────
  // The cabinet edits a FreightInputDraft (parsed from freightInputDraftJson);
  // the server validates + serializes the resulting FreightCharge into
  // freightChargeJson; freightSnapshotJson is the immutable submit snapshot.
  // freightInputDraftJson is the source of truth for cabinet state, including
  // the destination id, MM flag, truck class, discount, and toll overrides.
  freightInputDraftJson: string | null;
  freightChargeJson: string | null;
  freightSnapshotJson: string | null;
  lineItems: QuotationLineItemDto[];
  /** 14ykpqtr22u — kilogram rows dropped when the rows arrived (cutting lists take pieces only).
   *  Client-only; when nothing else is left the quotation shows the "Only kilogram quantities" note.
   *  14ykpqtw3hw: on extraction this also counts the kg lines the server dropped (repeats, totals). */
  droppedKgLineCount?: number;
  /** 14ykpqtr22u — the frozen record of what posted (Submitted only). Read-only; never sent on save. */
  submissionSnapshot?: QuotationSubmissionSnapshot | null;
  extractionStatus: ExtractionStatus;
  extractionError: string | null;
  extractionSource: 'manual' | 'intake';
  /** Kind of input used for extraction — present whenever extractionSource='intake' */
  extractionSourceKind: ExtractionSourceKind | null;
  /** Persisted for text extractions — lets retry work after page reload */
  extractionText: string | null;
  /** Display name only — Files cannot be persisted to sessionStorage */
  extractionFileName: string | null;
  /** Notice surfaced when the backend normalized missing quantities; null once dismissed. */
  extractionNotice: ExtractionNotice | null;
  createdAt: string;
  updatedAt: string;
  /** True when the local draft has unpersisted changes. Cleared on successful save / loadDraft / markSubmitted. Transient — never sent to the server. */
  dirty: boolean;
  /** Source email snapshot — non-null only when this draft was created by the email automation pipeline. */
  sourceEmail: QuotationSourceEmailSummary | null;
  /** Manually-uploaded attachments (CSC-283 Layer 3). Empty for email-sourced AutoDrafts and for text-only / single-file manual drafts. */
  attachments: QuotationAttachment[];
  /** Stage-2 extraction state — populated by the email pipeline. NotApplicable on manual drafts. */
  emailExtractionStatus: EmailExtractionStatus;
  /** True when the quotation has been archived (hidden from default views but still recoverable via restore). */
  isArchived: boolean;
  archivedAt: string | null;
  archivedById: number | null;
}

export interface QuotationListItemDto {
  id: number;
  quotationNumber: string;
  status: QuotationStatus;
  /** Backend ExtractionStatus column. Distinct from the manual-flow string-union
   *  ExtractionStatus that lives only on QuotationDraft. */
  extractionStatus: EmailExtractionStatus;
  customerName: string;
  projectName: string;
  projectCategoryName: string | null;
  preparedByName: string | null;
  createdAt: string;
  updatedAt: string | null;
  date: string;
  expirationDate: string;
  /** Piece rows only — kilogram rows never show in a quotation (14ykpqtr22u). */
  totalLineItems: number;
  /** 14ykpqtr22u — every line is in kilograms (typically a "per kg price" inquiry). */
  isKgOnly?: boolean;
  cuttingListNumber: string | null;
  netSuiteUrl: string | null;
  postedAt: string | null;
  postedByName: string | null;
  // Cheap source-email summary — populated only on AutoDraft rows.
  sourceEmailFrom: string | null;
  sourceEmailSubject: string | null;
  sourceEmailReceivedAt: string | null;
  sourceEmailAttachmentCount: number;
  sourceEmailGmailThreadId: string | null;
  // CSC-289: per-user read receipt — true when the current user has opened this
  // quotation at least once. Drives the workspace's unread dot.
  isRead: boolean;
  isArchived: boolean;
  archivedAt: string | null;
  archivedById: number | null;
}

export interface QuotationStats {
  draft: number;
  submitted: number;
  autoDraft: number;
  sent: number;
  total: number;
  /** CSC-297: count of AutoDraft rows the current user has not yet opened.
   *  Drives the "N new" pill in the workspace filter bar. */
  unreadAutoDraft: number;
  /** Separate tally for the Archived tab; mutually exclusive with the per-status counts above. */
  archived: number;
}

export interface QuotationListParams {
  status?: QuotationStatus;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
  /** When true, the paged query returns ONLY archived rows (the Archived tab).
   *  When false/omitted (default), archived rows are excluded — the two modes
   *  are mutually exclusive so the Archived tab doesn't double-count. */
  includeArchived?: boolean;
}

export interface QuotationListResult {
  items: QuotationListItemDto[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export type QuotationFilterTab = 'all' | 'auto-drafts' | 'drafts' | 'submitted' | 'archived';

export interface RecomputedCuttingListLine {
  matchedItemId: string;
  rate: number | null;
  rateWithMarkup: number | null;
}

export interface CuttingListGradeMarkupsWire {
  grade33: number;
  grade40: number;
  grade60: number;
  grade75: number;
  grade80: number;
}

export interface CreateCuttingListResult {
  quotationId: number;
  /** Cutting-list tran id from NetSuite (e.g. CL-000032997). */
  quotationNumber: string;
  /** CSC-293: post-submit DOP QD-XXX number that replaced the original DR-XXX. */
  newQuotationNumber: string;
  netSuiteUrl: string;
  postedAt: string;
  postedByName: string | null;
  // CSC-290: server-side recompute applied at submit. The store patches the
  // active draft's per-line `rateWithMarkup` and `gradeMarkupsJson` from these
  // so the form reflects what actually hit NetSuite. `baseMarkupPct` is
  // transitional (always 0 from CSC-290 onward; column is dead).
  baseMarkupPct: number;
  gradeMarkups: CuttingListGradeMarkupsWire;
  recomputedLines: RecomputedCuttingListLine[];
}
